import { type UsedSourceMetadatas } from "@do/shared-types";
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
} from "@do/ui/components/avatar";
import { Button } from "@do/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@do/ui/components/dropdown-menu";
import { cn } from "@do/ui/lib/utils";
import { useTranslations } from "next-intl";
import { Fragment } from "react";
import { useOptionalChat } from "../../../../providers/chat-provider";

const MAX_SOURCES_DISPLAYED = 3;

function avatarDisplayKey(s: UsedSourceMetadatas): string {
  const favicon = s.favicon_url.trim();
  return favicon.length > 0 ? favicon : `no-favicon|${s.site_name.trim()}|${s.page_title.trim()}`;
}

/** One avatar per distinct favicon; without favicon, dedupe by site + page title. */
function dedupeUsedSourcesForAvatars(sources: UsedSourceMetadatas[]): UsedSourceMetadatas[] {
  const seen = new Set<string>();
  const out: UsedSourceMetadatas[] = [];
  for (const s of sources) {
    const key = avatarDisplayKey(s);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

interface MessageActionSourcesProps {
  id: string;
  usedSourcesMetadatas: UsedSourceMetadatas[];
}

export const MessageActionSources = ({ id, usedSourcesMetadatas }: MessageActionSourcesProps) => {
  const t = useTranslations("do-chat-v2");
  const chat = useOptionalChat();
  const rewriteUrl = chat?.rewriteUrl;
  const uniqueSources = dedupeUsedSourcesForAvatars(usedSourcesMetadatas);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={`message-${id}-sources-button`}
          variant="ghost"
          className={cn(
            "size-fit group/message-actions-sources",
            "gap-(--message-actions-sources-gap)",
            "rounded-(--message-actions-border-radius)",
            "p-(--message-actions-padding)!",
            "bg-(--message-actions-background)/(--message-actions-background-opacity)",
            "hover:bg-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)",
            "text-(--message-actions-foreground)/(--message-actions-foreground-opacity)",
            "hover:text-(--message-actions-hover-foreground)/(--message-actions-hover-foreground-opacity)"
          )}
        >
          <AvatarGroup
            className={cn(
              "*:data-[slot=avatar]:transition-all -space-x-1",
              "*:data-[slot=avatar]:ring-(--message-actions-sources-ring-color)",
              "group-hover/message-actions-sources:*:data-[slot=avatar]:ring-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)"
            )}
          >
            {uniqueSources.slice(0, MAX_SOURCES_DISPLAYED).map((source) => (
              <Avatar key={avatarDisplayKey(source)} className="size-(--message-actions-icon-size)">
                <AvatarImage aria-hidden="true" src={source.favicon_url} />
                <AvatarFallback
                  className={cn(
                    "rounded-full",
                    "bg-(--message-actions-sources-icon-background)/(--message-actions-sources-icon-background-opacity)"
                  )}
                />
              </Avatar>
            ))}
            {uniqueSources.length > MAX_SOURCES_DISPLAYED && (
              <AvatarGroupCount
                className={cn(
                  "leading-none font-bold transition-all",
                  "text-(length:--message-actions-sources-icon-text-font-size)",
                  "size-(--message-actions-icon-size)",
                  "bg-(--message-actions-sources-icon-background)/(--message-actions-sources-icon-background-opacity)",
                  "text-(--message-actions-foreground)/(--message-actions-foreground-opacity)",
                  "ring-(--message-actions-sources-ring-color)",
                  "group-hover/message-actions-sources:ring-(--message-actions-hover-background)/(--message-actions-hover-background-opacity)"
                )}
              >
                <span>{uniqueSources.length - MAX_SOURCES_DISPLAYED}</span>
              </AvatarGroupCount>
            )}
          </AvatarGroup>{" "}
          <span className="leading-none pt-px text-(length:--message-actions-sources-text-font-size)">
            {t("chat.messages.actions.sources")}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          "w-(--message-actions-sources-content-width)",
          "max-h-(--message-actions-sources-content-max-height)!",
          "border-(--message-actions-sources-content-border-color)/(--message-actions-sources-content-border-opacity)"
        )}
      >
        {usedSourcesMetadatas.map((source, index) => (
          <Fragment key={`${index}-${avatarDisplayKey(source)}`}>
            <DropdownMenuItem
              className={cn(
                "flex w-full min-w-0 flex-col items-start gap-1",
                "focus:bg-(--message-actions-sources-content-item-hover-background)/(--message-actions-sources-content-item-hover-background-opacity)"
              )}
              asChild
            >
              <a
                href={rewriteUrl ? rewriteUrl(source.site_url) : source.site_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="flex w-full min-w-0 flex-row items-center gap-1">
                  <Avatar className="size-(--message-actions-icon-size) shrink-0 rounded-none!">
                    <AvatarImage src={source.favicon_url} />
                    <AvatarFallback className="rounded-full bg-(--message-actions-sources-icon-background)/(--message-actions-sources-icon-background-opacity)" />
                  </Avatar>
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      "font-(--message-actions-sources-content-site-name-font-weight)!",
                      "text-(--message-actions-sources-content-site-name-color)/(--message-actions-sources-content-site-name-color-opacity)",
                      "text-(length:--message-actions-sources-content-font-size)"
                    )}
                  >
                    {source.site_name}
                  </span>
                </div>

                <span
                  className={cn(
                    "w-full min-w-0",
                    "font-(--message-actions-sources-content-page-title-font-weight)!",
                    "line-clamp-(--message-actions-sources-content-page-title-line-clamp)",
                    "text-(length:--message-actions-sources-content-font-size)"
                  )}
                >
                  {source.page_title}
                </span>

                {source.description && (
                  <span
                    className={cn(
                      "text-(length:--message-actions-sources-content-font-size)",
                      "font-(--message-actions-sources-content-description-font-weight)!",
                      "text-(--message-actions-sources-content-description-color)/(--message-actions-sources-content-description-color-opacity)",
                      "line-clamp-(--message-actions-sources-content-description-line-clamp)"
                    )}
                  >
                    {source.description}
                  </span>
                )}
              </a>
            </DropdownMenuItem>
            {index < usedSourcesMetadatas.length - 1 ? <DropdownMenuSeparator /> : null}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
